import React, { useState, useEffect, useCallback } from "react";
import { Avatar, AvatarProps } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";

interface RetryImageProps extends Omit<AvatarProps, "src" | "onError"> {
  src?: string;
  maxRetries?: number;
  retryDelay?: number;
}

/**
 * Avatar wrapper that retries loading images on failure
 * instead of permanently caching the error state.
 */
export const RetryImage: React.FC<RetryImageProps> = ({
  src,
  maxRetries = 3,
  retryDelay = 2000,
  children,
  ...avatarProps
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [failed, setFailed] = useState(false);
  const [imgSrc, setImgSrc] = useState(src);

  useEffect(() => {
    setRetryCount(0);
    setFailed(false);
    setImgSrc(src);
  }, [src]);

  const handleError = useCallback(() => {
    if (retryCount < maxRetries) {
      setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        setImgSrc(src ? `${src}?retry=${retryCount + 1}` : undefined);
      }, retryDelay);
    } else {
      setFailed(true);
    }
  }, [retryCount, maxRetries, retryDelay, src]);

  return (
    <Avatar
      {...avatarProps}
      src={!failed && imgSrc ? imgSrc : undefined}
      onError={handleError}
    >
      {(!imgSrc || failed) && (children || <ImageIcon />)}
    </Avatar>
  );
};
